import { createRequestFactory } from "@/lib/request-factory";
import {
  ClientDocument,
  ClientPreferenceFragmentFragmentDoc,
  type ClientQuery,
  type ClientQueryVariables,
} from "@/__generated__/graphql";
import { useAuthState } from "@/store/auth";
import type { UserCreditAccount, UserInfo } from "@/types/users";
import { storage } from "@/lib/storage";
import { transformNotificationSettingsInput } from "@/containers/notifications/notification-settings/lib";
import { getFragmentData } from "@/__generated__";

type Response = {
  userInfo: UserInfo;
};

export const useClientStore = createRequestFactory<
  ClientQuery,
  Response,
  ClientQueryVariables
>({
  method: "query",
  graphqlDocument: ClientDocument,
  fetchPolicy: "network-only",
  transformFunction: (res) => {
    if (res.client) {
      const {
        id,
        user,
        contacts,
        addresses,
        accountType,
        creditAccounts,
        billingAccount,
        clientDisplayName,
      } = res.client;

      const preferences = getFragmentData(
        ClientPreferenceFragmentFragmentDoc,
        res.client.preferences,
      );

      const userCreditAccounts = creditAccounts.map(
        (account): UserCreditAccount => {
          let linkedObject: UserCreditAccount["linkedObject"] = null;

          if (account.linkedObject?.__typename === "Department") {
            linkedObject = {
              linkedType: "Department",
              id: account.linkedObject.id,
              name: account.linkedObject.name,
            };
          } else if (account.linkedObject?.__typename === "Package") {
            linkedObject = {
              linkedType: "Package",
              id: account.linkedObject.id,
            };
          }

          return {
            id: account.id,
            creditType: account.creditType,
            balance: account.balance,
            linkedObject,
          };
        },
      );

      const userInfo: Omit<UserInfo, "paymentMethod"> = {
        id,
        contacts,
        name: clientDisplayName,
        addresses: addresses.map((addr) => ({
          id: addr.id,
          label: addr.label ?? undefined,
          fullAddress: addr.fullAddress,
          postalCode: addr.postalCode,
          primary: addr.primary,
          active: addr.active,
          unitNumber: addr.unitNumber ?? undefined,
        })),
        user: {
          id: user.id,
          email: user.email,
          phoneNumber: user.phoneNumber ?? undefined,
          phoneNumberVerifiedAt: user.phoneNumberVerifiedAt ?? undefined,
          emailVerifiedAt: user.emailVerifiedAt ?? undefined,
        },
        firstName: contacts[0].firstName,
        lastName: contacts[0].lastName,
        accountType,
        creditAccounts: userCreditAccounts,
        autoPay: preferences.autoPay ?? false,
        billingAccount: {
          id: billingAccount.id,
          balance: billingAccount.balance,
          overdueAmount: billingAccount.overdueAmount ?? 0,
          totalOverdueInvoice: billingAccount.totalOverdueInvoice ?? 0,
          invoicesHitsMaxOverdueDays:
            billingAccount.invoicesHitsMaxOverdueDays ?? false,
        },
        notificationSettings: transformNotificationSettingsInput(
          preferences?.automatedNotificationEnabled?.categories,
          preferences?.automatedNotificationEnabled?.channels,
        ),
      };

      return { userInfo } as Response;
    }

    storage.deleteAll();
    throw new Error("Request Failed");
  },
  onFetchSuccess: (data) => {
    const { setUserInfo } = useAuthState.getState();
    setUserInfo(data.userInfo);
  },
});
